import type { ConsultantLicenseInterface, LicenseListResponse, LicenseUpdateRequest } from 'types';
import { emptyConsultantLicense } from 'utils/validations/license';

export const LICENSE_COUNTRIES = ['Australia', 'Canada'];
export const MAX_LICENSES = 10;

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const readString = (value: unknown): string => {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return '';
};

const readId = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    return null;
};

const toFormDate = (value: unknown): string => {
    const text = readString(value);
    if (!text) return '';
    return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : text;
};

const asRecordList = (raw: unknown): Record<string, unknown>[] => {
    if (Array.isArray(raw)) {
        return raw.filter(isRecord);
    }
    if (isRecord(raw) && Array.isArray(raw.licenses)) {
        return raw.licenses.filter(isRecord);
    }
    return [];
};

export const normalizeLicenses = (raw: unknown): ConsultantLicenseInterface[] => {
    const licenses = asRecordList(raw)
        .map((item) => ({
            id: readId(item.id),
            license_number: readString(item.license_number),
            country: readString(item.country),
            date_issued: toFormDate(item.date_issued),
        }))
        .filter((item) => item.id || item.license_number || item.country || item.date_issued);

    return licenses.length ? licenses : [{ ...emptyConsultantLicense }];
};

export const toLicenseUpdatePayload = (
    licenses: ConsultantLicenseInterface[]
): LicenseUpdateRequest => ({
    licenses: licenses.map((item) => ({
        ...(item.id ? { id: item.id } : {}),
        country: item.country.trim(),
        license_number: item.license_number.trim(),
        date_issued: item.date_issued,
    })),
});

export const licensesFromResponse = (raw: LicenseListResponse): ConsultantLicenseInterface[] =>
    normalizeLicenses(raw);
